import { useCallback, useState } from "react";
import { ArrowUpRight as ArrowUpRightIcon } from "@tamagui/lucide-icons";
import { Body3, Button, Card, Display2, Display6, getTokenValue, Subtitle2, shadow, useFocusEffect, viewTransitionName, XStack, YStack } from "@medbillai/ui";
import { coverageWhereYouStandUrl } from "@medbillai/utils/urls";
import { ZoomLink, zoomHidesSource } from "../../../components/ZoomLink";
import { FigureChart, isFirstCheck } from "../chart/FigureChart";
import { useCoveragePlanStore } from "../coveragePlanStore";
import { finalFigureText, money, planEndedText, planYearEndedText, shortDate } from "../format";
import { PressableStack } from "../PressableStack";
import type { CoveragePlan, HeadlineFigure } from "../read/readCoverage";
import { pickedHeadline } from "../where-you-stand/tiers";
const CARD_CHART_HEIGHT = getTokenValue("$si11", "size");
const CARD_LIMIT_TOP = getTokenValue("$sp0.75", "space");
const CARD_SURFACE = {
  flexDirection: "column",
  backgroundColor: "$surfaceInverse",
  borderRadius: "$br3",
  ...shadow.subtle,
  padding: "$sp2",
  paddingTop: "$sp2.5",
  paddingBottom: "$sp1.5",
  gap: "$sp1"
} as const;
type HeadlineCardProps = {
  plan: CoveragePlan;
  today: Date;
  onLinkInsurance: (source: "firstCheck" | "noTotals") => void;
  onOpen: () => void;
};

/** What the card shows for the plan; the root's tracked state follows it. */
export const headlineState = (plan: CoveragePlan) => plan.planYearEnded && (!plan.ended || !plan.headline) ? {
  kind: "planYearEnded"
} as const : plan.headline ? {
  kind: "totals",
  headline: plan.headline
} as const : {
  kind: "noTotals"
} as const;
export function HeadlineCard({
  plan,
  today,
  onLinkInsurance,
  onOpen
}: HeadlineCardProps) {
  const state = headlineState(plan);
  const picked = useCoveragePlanStore(s => s.tier);
  const [opening, setOpening] = useState(false);
  useFocusEffect(useCallback(() => setOpening(false), []));
  if (state.kind === "planYearEnded") {
    return <Card variant="elevated" size="large">
        <Subtitle2 color="$textDefault">
          {plan.ended ? `${planEndedText(plan)}. Its final totals aren’t available.` : planYearEndedText(plan)}
        </Subtitle2>
      </Card>;
  }
  if (state.kind === "noTotals") {
    return <Card variant="raised" size="large" padding="$sp2" gap="$sp2">
        <Subtitle2 color="$textDefault">
          {plan.payerName} didn’t report totals
        </Subtitle2>
        <Button variant="brand" onPress={() => onLinkInsurance("noTotals")}>
          Link your insurance account
        </Button>
      </Card>;
  }
  const headline = pickedHeadline(plan, picked) ?? state.headline;
  const {
    limit
  } = headline;
  const label = figureLabel(headline);
  const history = plan.histories.inNetwork[headline.kind][headline.tier];
  // An ended year's final totals have nothing more to open.
  const opens = !plan.planYearEnded;
  const body = <>
      <XStack alignItems="center" gap="$sp1">
        <XStack gap="$sp1" alignItems="baseline" flex={1} flexWrap="wrap">
          <Subtitle2 color="$textInverse">{label}</Subtitle2>
          <Body3 color="$textInverseFaint">
            {plan.ended ? finalFigureText(plan, headline) : checkedLabel(plan)}
          </Body3>
        </XStack>
        {opens ? <OpenMark /> : null}
      </XStack>
      <XStack alignItems="baseline" gap="$sp0.25">
        <Display2 color="$textInverse">{money(headline.met)}</Display2>
        {limit === null ? null : <Display6 color="$textInverseMuted">
            / {money(limit)}
            {limit > 0 && (headline.remaining ?? limit - headline.met) <= 0 ? " · Met" : ""}
          </Display6>}
      </XStack>
      {limit === null ? null : <YStack marginTop="$sp2" {...viewTransitionName("hero-chart")}>
          <FigureChart plan={plan} headline={headline} deductible={plan.inNetwork.deductible[headline.tier]} scrub={false} history={history} today={today} height={CARD_CHART_HEIGHT} bleed="$sp2" limitLeft="$sp2" limitTop={CARD_LIMIT_TOP} strokeWidth={2} axisGap="$sp0.5" />
        </YStack>}
    </>;
  return <YStack data-sentry-element="YStack" data-sentry-component="HeadlineCard" data-sentry-source-file="HeadlineCard.tsx">
      {/* The tuck slides under the card. iOS wraps the card in the zoom
          link's native view, so the stacking order sits on this wrapper. */}
      <YStack zIndex={1} data-sentry-element="YStack" data-sentry-source-file="HeadlineCard.tsx">
        {opens ? <ZoomLink href={coverageWhereYouStandUrl()} onOpen={onOpen} onPressStart={() => setOpening(true)}>
            {onPress => <PressableStack onPress={onPress} aria-label={`${label}, ${money(headline.met)}${limit === null ? "" : ` of ${money(limit)}`}`} {...CARD_SURFACE} {...viewTransitionName("hero-shell")}>
                <YStack opacity={opening && zoomHidesSource() ? 0 : 1}>
                  {body}
                </YStack>
              </PressableStack>}
          </ZoomLink> : <YStack {...CARD_SURFACE}>{body}</YStack>}
      </YStack>
      {limit !== null && !plan.ended && isFirstCheck({
      history,
      confirmedAt: plan.confirmedAt
    }) ? <FirstCheckTuck onLink={() => onLinkInsurance("firstCheck")} /> : null}
    </YStack>;
}

/** The card opens Where you stand; the mark only says so. */
function OpenMark() {
  return <XStack aria-hidden width="$si4" height="$si4" borderRadius="$br1" backgroundColor="$utilityOverlayInverse" alignItems="center" justifyContent="center" data-sentry-element="XStack" data-sentry-component="OpenMark" data-sentry-source-file="HeadlineCard.tsx">
      <ArrowUpRightIcon size="$iconSmall" color="$textInverseBrand" data-sentry-element="ArrowUpRightIcon" data-sentry-source-file="HeadlineCard.tsx" />
    </XStack>;
}
function FirstCheckTuck({
  onLink
}: {
  onLink: () => void;
}) {
  return <XStack backgroundColor="$bgBrand" borderBottomLeftRadius="$br3" borderBottomRightRadius="$br3" marginTop="$-sp3" paddingTop="$sp5" paddingBottom="$sp2" paddingHorizontal="$sp2" alignItems="center" gap="$sp1.5" data-sentry-element="XStack" data-sentry-component="FirstCheckTuck" data-sentry-source-file="HeadlineCard.tsx">
      <YStack flex={1} gap="$sp0.25" data-sentry-element="YStack" data-sentry-source-file="HeadlineCard.tsx">
        <Body3 color="$textSubtle" data-sentry-element="Body3" data-sentry-source-file="HeadlineCard.tsx">Only today’s totals so far.</Body3>
        <Subtitle2 color="$textDefault" data-sentry-element="Subtitle2" data-sentry-source-file="HeadlineCard.tsx">Link to see the year so far</Subtitle2>
      </YStack>
      <Button variant="inverse" size="small" fillWidth={false} onPress={onLink} data-sentry-element="Button" data-sentry-source-file="HeadlineCard.tsx">
        Link securely
      </Button>
    </XStack>;
}
const figureLabel = ({
  kind,
  tier
}: HeadlineFigure) => `${tier === "family" ? "Family in" : "In"} network ${kind === "deductible" ? "deductible" : "out-of-pocket max"}`;
const checkedLabel = (plan: CoveragePlan) => plan.staleSince ? `Last confirmed ${shortDate(plan.staleSince)}` : plan.confirmedAt ? shortDate(plan.confirmedAt) : "";